import { useEffect, useRef, useState } from "react";
import { api, tokenStore, LOGO_WHITE } from "../lib/api";
import { useAuth } from "../lib/auth";
import AdminLayout from "./AdminLayout";

// PIN-protected entrance for /admin. Validates 4-digit code against backend.
export default function AdminPinGate() {
    const { user, loading, refresh } = useAuth();
    const [digits, setDigits] = useState(["", "", "", ""]);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const inputs = useRef([]);

    useEffect(() => {
        if (!loading && (!user || user.role !== "admin")) {
            inputs.current[0]?.focus();
        }
    }, [loading, user]);

    if (loading) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="admin-pin-loading">
                <div className="text-white/50">Caricamento…</div>
            </div>
        );
    }

    if (user && user.role === "admin") {
        return <AdminLayout />;
    }

    const submitPin = async (pin) => {
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.post("/auth/pin", { pin });
            if (data.access_token) tokenStore.set(data.access_token);
            await refresh();
        } catch (e) {
            const detail = e.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Codice non valido");
            setDigits(["", "", "", ""]);
            setTimeout(() => inputs.current[0]?.focus(), 80);
        } finally {
            setSubmitting(false);
        }
    };

    const setDigit = (i, value) => {
        const v = (value || "").replace(/[^0-9]/g, "").slice(0, 1);
        const next = [...digits];
        next[i] = v;
        setDigits(next);
        if (v && i < 3) inputs.current[i + 1]?.focus();
        if (next.every((d) => d.length === 1) && !submitting) {
            submitPin(next.join(""));
        }
    };

    const onKeyDown = (i, e) => {
        if (e.key === "Backspace" && !digits[i] && i > 0) {
            inputs.current[i - 1]?.focus();
        }
    };

    const onPaste = (e) => {
        const text = (e.clipboardData?.getData("text") || "").replace(/[^0-9]/g, "").slice(0, 4);
        if (text.length === 4) {
            e.preventDefault();
            const next = text.split("");
            setDigits(next);
            inputs.current[3]?.blur();
            submitPin(text);
        }
    };

    return (
        <div className="min-h-screen bg-black text-white flex items-center justify-center p-6" data-testid="admin-pin-page">
            <div className="w-full max-w-md text-center">
                <img src={LOGO_WHITE} alt="N&M" className="h-12 mx-auto mb-12" />
                <div className="kicker mb-3">Area riservata</div>
                <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-[-0.03em] mb-4">
                    Inserisci il codice
                </h1>
                <p className="text-white/60 mb-10">Codice di accesso a 4 cifre</p>

                <div className="flex justify-center gap-3 md:gap-4 mb-6" onPaste={onPaste}>
                    {digits.map((d, i) => (
                        <input
                            key={i}
                            ref={(el) => (inputs.current[i] = el)}
                            type="tel"
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={1}
                            value={d}
                            disabled={submitting}
                            onChange={(e) => setDigit(i, e.target.value)}
                            onKeyDown={(e) => onKeyDown(i, e)}
                            className="w-14 h-16 md:w-16 md:h-20 bg-transparent border border-white/25 focus:border-[color:var(--brand-accent)] outline-none text-center font-display text-3xl md:text-4xl font-bold transition-colors"
                            data-testid={`pin-digit-${i}`}
                        />
                    ))}
                </div>

                <div className="h-6 mb-4">
                    {submitting && <div className="text-white/60 text-sm">Verifica…</div>}
                    {error && (
                        <div className="text-red-400 text-sm" data-testid="admin-pin-error">
                            {error}
                        </div>
                    )}
                </div>

                <p className="text-xs text-white/40 mt-12">Accesso riservato al personale autorizzato.</p>
            </div>
        </div>
    );
}
